import { formatSignedRupeeLabel } from '../../../../display-values/lumpsumPlanAmount'
import { useState } from 'react'
import type { LumpsumPlanRecommendationTaxData } from '../../api/recommendation.types'
import NameValueTableRow from '../../../../../../../components/ui-components/NameValueTableRow'
import InPlaceNameValueAccordion from '../../../../../../../components/ui-components/InPlaceNameValueAccordion'
import { TaxLiabilityAccordionState } from './ExitLoadTaxLiabilityAccordion'

type ExitLoadTaxLiabilityAccordionBodyProps = {
  taxLiabilityState: TaxLiabilityAccordionState
  taxData: LumpsumPlanRecommendationTaxData | null
  withdrawalAmount: number
  currentValue: number | null
  totalExitLoad: number
  totalTaxLiability: number
}

export function ExitLoadTaxLiabilityAccordionBody({
  taxLiabilityState,
  taxData,
  withdrawalAmount,
  currentValue,
  totalExitLoad,
  totalTaxLiability
}: ExitLoadTaxLiabilityAccordionBodyProps) {
  return (
    <div className="pb-4 flex flex-col gap-4">
      <ExitLoadTaxLiabilityContent
        taxLiabilityState={taxLiabilityState}
        taxData={taxData}
        withdrawalAmount={withdrawalAmount}
        currentValue={currentValue}
        totalExitLoad={totalExitLoad}
        totalTaxLiability={totalTaxLiability}
      />
    </div>
  )
}

type ExitLoadTaxLiabilityContentProps = {
  taxLiabilityState: TaxLiabilityAccordionState
  taxData: LumpsumPlanRecommendationTaxData | null
  withdrawalAmount: number
  currentValue: number | null
  totalExitLoad: number
  totalTaxLiability: number
}

function ExitLoadTaxLiabilityContent({
  taxLiabilityState: _taxLiabilityState,
  taxData,
  withdrawalAmount,
  currentValue,
  totalExitLoad,
  totalTaxLiability
}: ExitLoadTaxLiabilityContentProps) {
  // Full withdrawal case is not used for now — always render partial. Restore later.
  // if (_taxLiabilityState === TaxLiabilityAccordionState.FULL_WITHDRAWAL) {
  //   return (
  //     <FullWithdrawalTaxLiabilityState
  //       withdrawalAmount={withdrawalAmount}
  //       totalExitLoad={totalExitLoad}
  //       totalTaxLiability={totalTaxLiability}
  //       taxData={taxData}
  //     />
  //   )
  // }

  return (
    <PartialWithdrawalTaxLiabilityState
      currentValue={currentValue}
      withdrawalAmount={withdrawalAmount}
      totalExitLoad={totalExitLoad}
      totalTaxLiability={totalTaxLiability}
      taxData={taxData}
    />
  )
}

export function PartialWithdrawalTaxLiabilityState({
  currentValue,
  withdrawalAmount,
  totalExitLoad,
  totalTaxLiability,
  taxData
}: {
  currentValue: number | null
  withdrawalAmount: number
  totalExitLoad: number
  totalTaxLiability: number
  taxData: LumpsumPlanRecommendationTaxData | null
}) {
  const [isEstimatedTaxExpanded, setIsEstimatedTaxExpanded] = useState(false)

  return (
    <>
      <div className="flex flex-col gap-[11px]">
        {currentValue != null ? (
          <TaxSummaryRow label="Current Value" value={`${formatSignedRupeeLabel({ rawAmount: currentValue })}`} />
        ) : null}
        <TaxSummaryRow label="Redeemable amount" value={`${formatSignedRupeeLabel({ rawAmount: withdrawalAmount })}`} />
        <TaxSummaryRow label="Exit Load" value={`${formatSignedRupeeLabel({ rawAmount: totalExitLoad })}`} />

        <div
          style={{
            width: isEstimatedTaxExpanded ? 'calc(100% + 24px)' : '100%',
            marginLeft: isEstimatedTaxExpanded ? '-12px' : '0px'
          }}
        >
          <InPlaceNameValueAccordion
            name="Estimated Tax Liability"
            value={`${formatSignedRupeeLabel({ rawAmount: totalTaxLiability })}`}
            isExpanded={isEstimatedTaxExpanded}
            alwaysShowValue
            onAccordionClick={() => setIsEstimatedTaxExpanded((currentExpanded) => !currentExpanded)}
            nameClassName="!text-[#6A6E6E] !text-[14px] !font-[400]"
            valueClassName="!text-[#000000] !text-[14px] !font-[500]"
            accordionClassName={isEstimatedTaxExpanded ? '!bg-[#F0F4F7]' : ''}
          >
            <TaxBreakupSection
              title="STCG Tax @ 20%"
              taxAmount={taxData?.stcgTaxInfo?.taxAmount ?? 0}
              investmentAmount={taxData?.stcgTaxInfo?.investedAmount ?? 0}
              gainsAmount={taxData?.stcgTaxInfo?.gains ?? 0}
            />
            <TaxBreakupSection
              title="LTCG Tax @ 12.5%"
              taxAmount={taxData?.ltcgTaxInfo?.taxAmount ?? 0}
              investmentAmount={taxData?.ltcgTaxInfo?.investedAmount ?? 0}
              gainsAmount={taxData?.ltcgTaxInfo?.gains ?? 0}
            />
          </InPlaceNameValueAccordion>
        </div>
      </div>
      <div className="flex gap-2 items-center w-full">
        <TaxLiabilityAccentBar />
        <div className="text-[14px] font-[400] leading-[140%] text-[#00661D]">
          {isEstimatedTaxExpanded
            ? 'This recommendation accounts for the above exit load and tax implications'
            : 'This recommendation accounts for the above exit load and tax liability'}
        </div>
      </div>
    </>
  )
}

/* Full withdrawal UI — unused for now; restore with the branch in ExitLoadTaxLiabilityContent
export function FullWithdrawalTaxLiabilityState({
  withdrawalAmount,
  totalExitLoad,
  totalTaxLiability,
  taxData
}: {
  withdrawalAmount: number
  totalExitLoad: number
  totalTaxLiability: number
  taxData: LumpsumPlanRecommendationTaxData | null
}) {
  const [isFullWithdrawalCalculationExpanded, setIsFullWithdrawalCalculationExpanded] = useState(false)
  const [isEstimatedTaxExpanded, setIsEstimatedTaxExpanded] = useState(false)

  return (
    <>
      <div className="flex gap-2 items-start w-full">
        <TaxLiabilityAccentBar />
        <div className="text-[14px] font-[400] leading-[140%] text-[#00661D]">
          We minimise your tax by withdrawing from the folio with the oldest investments first, so you pay the least possible tax. Please verify the exact tax details on your broker platform.
        </div>
      </div>
      {isFullWithdrawalCalculationExpanded ? (
        <div className="flex flex-col gap-[18px] w-full">
          <TaxSummaryRow label="Withdrawal amount" value={`${formatSignedRupeeLabel({ rawAmount: withdrawalAmount })}`} />
          <TaxSummaryRow label="Exit Load" value={`${formatSignedRupeeLabel({ rawAmount: totalExitLoad })}`} />
          <div
            style={{
              width: isEstimatedTaxExpanded ? 'calc(100% + 24px)' : '100%',
              marginLeft: isEstimatedTaxExpanded ? '-12px' : '0px'
            }}
          >
            <InPlaceNameValueAccordion
              name="Estimated Tax Liability"
              value={`${formatSignedRupeeLabel({ rawAmount: totalTaxLiability })}`}
              isExpanded={isEstimatedTaxExpanded}
              alwaysShowValue
              onAccordionClick={() => setIsEstimatedTaxExpanded((currentExpanded) => !currentExpanded)}
              nameClassName="!text-[#6A6E6E] !text-[14px] !font-[400]"
              valueClassName="!text-[#000000] !text-[14px] !font-[500]"
              accordionClassName={isEstimatedTaxExpanded ? '!bg-[#F0F4F7]' : ''}
            >
              <TaxBreakupSection
                title="STCG Tax @ 20%"
                taxAmount={taxData?.stcgTaxInfo?.taxAmount ?? 0}
                investmentAmount={taxData?.stcgTaxInfo?.investedAmount ?? 0}
                gainsAmount={taxData?.stcgTaxInfo?.gains ?? 0}
              />
              <TaxBreakupSection
                title="LTCG Tax @ 12.5%"
                taxAmount={taxData?.ltcgTaxInfo?.taxAmount ?? 0}
                investmentAmount={taxData?.ltcgTaxInfo?.investedAmount ?? 0}
                gainsAmount={taxData?.ltcgTaxInfo?.gains ?? 0}
              />
            </InPlaceNameValueAccordion>
          </div>
        </div>
      ) : (
        <div
          className="text-[14px] font-[400] leading-[130%] text-[#6A6E6E] underline underline-offset-2 cursor-pointer flex items-center gap-2 w-fit"
          onClick={() => setIsFullWithdrawalCalculationExpanded((currentExpanded) => !currentExpanded)}
        >
          View calculation for 100% withdrawal
          <TaxLiabilityDownChevron />
        </div>
      )}
    </>
  )
}

export function TaxLiabilityDownChevron() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="10" viewBox="0 0 12 10" fill="none" aria-hidden>
      <path
        d="M2 3L6 7L10 3"
        stroke="#6A6E6E"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
*/

export function TaxSummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <NameValueTableRow
      name={label}
      value={value}
      nameClassName="!text-[#6A6E6E] !text-[14px] !font-[400]"
      valueClassName="!text-[#000000] !text-[14px] !font-[500]"
      nameValueRowClassName="!items-center"
    />
  )
}

export function TaxBreakupSection({
  title,
  taxAmount,
  investmentAmount,
  gainsAmount
}: {
  title: string
  taxAmount: number
  investmentAmount: number
  gainsAmount: number
}) {
  return (
    <div className="pt-3 flex flex-col gap-3">
      <NameValueTableRow
        name={title}
        value={`${formatSignedRupeeLabel({ rawAmount: taxAmount })}`}
        nameClassName="!text-[#095EB2] !text-[13px] !font-[500]"
        valueClassName="!text-[#095EB2] !text-[13px] !font-[500]"
      />
      <div className="flex flex-col gap-2">
        <TaxBreakupRow label={title.includes('STCG') ? 'Investment held for less than 1Y' : 'Investment held for 1Y or more'} value={`${formatSignedRupeeLabel({ rawAmount: investmentAmount })}`} />
        <TaxBreakupRow label={title.includes('STCG') ? 'Short term capital gains' : 'Long term capital gains'} value={`${formatSignedRupeeLabel({ rawAmount: gainsAmount })}`} />
      </div>
    </div>
  )
}

export function TaxBreakupRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 text-[12px] leading-[130%]">
      <div className="font-[400] text-[#6A6E6E]">{label}</div>
      <div className="font-[500] text-[#6A6E6E]">{value}</div>
    </div>
  )
}

export function TaxLiabilityAccentBar() {
  return (
    <div
      className="shrink-0 self-stretch rounded-[2px] bg-[#00661D]"
      style={{ width: 3, minWidth: 3 }}
      aria-hidden
    />
  )
}
